<template>
  <div class="bg-white w-full">
    <h1 class="text-xl mb-5">Share video</h1>
    <div class="mb-5"> <!-- Property / Video details -->
      <div v-if="Object.keys(videoDetails).length > 0" class="flex">
        <div
          v-if="videoDetails.image"
          class="flex items-center justify-center w-24 h-24 mr-3 rounded border"
          style="background-size: cover; background-color: #eeeeee;"
          :style="{
            'background-image': `url(${videoDetails.image})`
          }"
        >
        </div>
        <div class="flex flex-col justify-center text-left">
          <strong v-if="videoDetails.name">{{ videoDetails.name }}</strong>
          <strong class="mt-2" v-if="displayAddress">{{ displayAddress }}</strong>
          <span class="block mt-2" v-if="videoDetails.price">{{ videoDetails.price }}</span>
        </div>
      </div>
    </div>
    <div class="w-full flex mb-5 border-b"> <!-- Tabs -->
      <div
        class="flex w-full bg-grey-lighter justify-center rounded-t items-center p-3 mr-2 cursor-pointer border -mb-px"
        :class="{
          'bg-white': selectedTab === 'facebook',
          'border-b-0': selectedTab === 'facebook'
        }"
        @click="selectedTab = 'facebook'"
      >
        <i class="fab fa-facebook mr-2"></i>
        <span class="mr-4">Facebook</span>
      </div>

      <div
        class="flex w-full bg-grey-lighter justify-center rounded-t items-center p-3 cursor-pointer border -mb-px"
        :class="{
          'bg-white' : selectedTab === 'twitter',
          'border-b-0': selectedTab === 'twitter'
        }"
        @click="selectedTab = 'twitter'"
      >
        <i class="fas fa-x mr-2"></i>
        <span class="mr-4">X</span>
      </div>
    </div>

    <div class="w-full text-left mb-5">
      <div v-if="selectedTab === 'facebook'">
        <div v-if="connectedAccount('facebook')">
          <div class="mb-4">
            <label class="mb-2 block">Share to page</label>
            <loader v-if="facebook.loadingPages" />
            <pl-select
              v-else
              class="w-full"
              select-class="select-sm rounded"
              v-model="facebook.selectedPage"
            >
              <option v-for="page in facebook.pages" :value="page.id" :key="page.id">{{ page.name }}</option>
            </pl-select>
          </div>
          <div>
            <label class="mb-2 block">Description</label>
            <textarea v-model="facebook.description" class="w-full input rounded"></textarea>
          </div>
        </div>
        <div v-else>
          You need to connect a Facebook account. Please go to the <router-link to="/profile-v2">Profile</router-link> page to get connected.
        </div>
      </div>

      <div v-if="selectedTab === 'twitter'">
        <div v-if="connectedAccount('twitter')">
          <div>
            <label class="mb-2 block">Tweet</label>
            <textarea v-model="twitter.tweet" maxlength="280" class="w-full input rounded"></textarea>
            <small class="block mt-1 w-full text-right text-xs text-grey-dark">
              {{ twitter.tweet.length }} / 280
            </small>
          </div>
        </div>
        <div v-else>
          You need to connect a Twitter account. Please go to the <router-link to="/profile-v2">Profile</router-link> page to get connected.
        </div>
      </div>
    </div>
    <div class="border-t pt-2 flex items-center justify-end"> <!-- Action bar -->
      <button v-if="selectedTab === 'facebook'" @click="share" :disabled="sharing || facebook.loadingPages" class="btn bg-blue text-white rounded py-2 px-4">
        <template v-if="!sharing">Share Video on Facebook</template>
        <loader fillColor='white' v-else />
      </button>
      <button v-if="selectedTab === 'twitter'" @click="share" :disabled="sharing" class="btn bg-blue text-white rounded py-2 px-4">
        <template v-if="!sharing">Share Video on Twitter</template>
        <loader fillColor='white' v-else />
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import api from '@/http'

export default {
  name: 'VideoShare',
  props: {
    videoId: {
      type: Number,
      required: true
    }
  },

  mounted () {
    this.getVideoDetails()
    this.getFacebookPages()
  },

  computed: {
    ...mapState(['profile', 'profile_v2']),

    displayAddress () {
      if (this.videoDetails.display_address === this.videoDetails.name) {
        return
      }

      return this.videoDetails.display_address
    }
  },

  data () {
    return {
      loadingPages: true,
      sharing: true,
      shared: false,
      videoDetails: {},
      facebook: {
        enabled: true,
        loadingPages: true,
        pages: [],
        selectedPage: null,
        description: ''
      },
      twitter: {
        enabled: false,
        tweet: ''
      },
      selectedTab: 'facebook'
    }
  },

  methods: {

    ...mapActions([
      'displayToastNotification',
      'fetchProfile'
    ]),

    getVideoDetails () {
      api.get('social/video-details', {
        params: {
          video_id: this.videoId
        }
      }).then(({ data }) => {
        this.videoDetails = data
      })
    },

    getFacebookPages () {
      this.facebook.loadingPages = true
      this.facebook.pages = []
      api.get('social/facebook/pages')
        .then(({ data }) => {
          this.facebook.pages = data
          this.facebook.selectedPage = this.facebook.pages[0].id
        }).catch(({ response }) => {
          this.onError(response)
        }).finally(() => {
          this.facebook.loadingPages = false
          this.sharing = false
        })
    },

    share () {
      this.sharing = true
      let payload = {
        video_id: this.videoId,
        platforms: []
      }

      if (this.selectedTab === 'facebook') {
        payload.platforms.push({
          name: 'facebook',
          pageId: this.facebook.selectedPage,
          description: this.facebook.description
        })
      }

      if (this.selectedTab === 'twitter') {
        payload.platforms.push({
          name: 'twitter',
          tweet: this.twitter.tweet
        })
      }

      api.post(`social/share`, payload).then(response => {
        this.shared = true
        this.displayToastNotification({
          type: 'success',
          icon: 'fa-bell',
          title: 'Successfully shared',
          info: 'Video shared to ' + this.selectedTab
        })
        if (this.selectedTab === 'twitter') {
          if (this.facebook.description.length > 0) {
            this.selectedTab = 'facebook'
            this.twitter.tweet = ''
          }
        } else if (this.selectedTab === 'facebook') {
          if (this.twitter.tweet.length > 0) {
            this.selectedTab = 'twitter'
            this.facebook.description = ''
          }
        }
      }).catch(({ response }) => {
        this.onError(response)
      }).finally(() => {
        this.sharing = false
      })
    },
    onError (response) {
      if (response) {
        if (response.data.action && response.data.action === 'reconnect-account') {
          this.fetchProfile()
        }

        this.displayToastNotification({
          type: 'warning',
          icon: 'fa-bell',
          title: 'There was an error',
          info: response.data.message
        })
      }
    },

    connectedAccount (name) {
      if (this.profile_v2.socialNetworks !== undefined) {
        return this.profile_v2.socialNetworks.filter(network => network.name === name).length > 0
      }
    }
  }
}
</script>

<style lang="scss" scoped>
input[type="checkbox"]:checked + span {
  border-color: #65ad3d; /* pl-green */
  color: #65ad3d;
  &:before {
    font-weight: 900;
    font-size: 0.8rem;
    font-family: "Font Awesome 5 Free";
    content: "\f00c";
  }
}

.tooltip {
  background: black;
  border-radius: 999999px;
  padding: 5px;
  color: white;
  font-size: 0.8rem;

  &::after {
    content: '';
    width: 0;
    height: 0;

    position: absolute;

    left: 50%;
    bottom: -8px;
    transform: translateX(-50%);

    border-right: 8px solid transparent;
    border-left: 8px solid transparent;

    border-top: 8px solid black;
  }
}

.checkbox-container label:hover + div {
  display: block;
}

</style>
